import { AnalyticsEvent, EventItem } from '../types'
import { Transport } from './Transport'
import { ConsentStore } from '../consent/ConsentStore'

interface Ga4Item {
  item_id: string
  item_name: string
  item_category?: string
  item_variant?: string
  price: number
  discount?: number
  quantity: number
  index?: number
}

function toGa4Item(item: EventItem, index?: number): Ga4Item {
  const g: Ga4Item = {
    item_id: item.id,
    item_name: item.name,
    price: item.price,
    quantity: item.quantity,
  }
  if (item.category) g.item_category = item.category
  if (item.variant) g.item_variant = item.variant
  if (item.discount !== undefined) g.discount = item.discount
  if (index !== undefined) g.index = index
  return g
}

type DataLayerPush = (obj: Record<string, unknown>) => void

function getDataLayer(): DataLayerPush | null {
  if (typeof window === 'undefined') return null
  if (!Array.isArray(window.dataLayer)) return null
  return (obj: Record<string, unknown>) => window.dataLayer.push(obj)
}

export class Ga4ClientTransport implements Transport {
  readonly name = 'Ga4ClientTransport'

  private readonly consent: ConsentStore

  constructor(consent: ConsentStore) {
    this.consent = consent
  }

  send(event: AnalyticsEvent): void {
    // Purchase is emitted server-side via GA4 Measurement Protocol. Never fire it browser-side.
    if (event.name === 'purchase') return

    if (event.consent.analytics_storage !== 'granted') return
    const push = getDataLayer()
    if (!push) return

    const { name, event_id, payload } = event

    switch (name) {
      case 'page_view': {
        let page_path = ''
        let page_title = ''
        try {
          const u = new URL(event.event_source_url)
          page_path = u.pathname + u.search
        } catch {
          page_path = event.event_source_url
        }
        if (typeof document !== 'undefined') {
          page_title = document.title
        }
        push({ event: 'page_view', event_id, page_path, page_title })
        break
      }

      case 'view_item': {
        push({ ecommerce: null })
        push({
          event: 'view_item',
          event_id,
          ecommerce: {
            currency: payload.currency,
            value: payload.value,
            items: payload.items?.map(toGa4Item) ?? [],
          },
        })
        break
      }

      case 'view_item_list': {
        push({ ecommerce: null })
        push({
          event: 'view_item_list',
          event_id,
          ecommerce: {
            item_list_id: payload.list_id,
            item_list_name: payload.list_name,
            items: payload.items?.map((item, i) => toGa4Item(item, i)) ?? [],
          },
        })
        break
      }

      case 'search': {
        push({ event: 'search', event_id, search_term: payload.search_term })
        break
      }

      case 'add_to_cart': {
        push({ ecommerce: null })
        push({
          event: 'add_to_cart',
          event_id,
          ecommerce: {
            currency: payload.currency,
            value: payload.value,
            items: payload.items?.map(toGa4Item) ?? [],
          },
        })
        break
      }

      case 'remove_from_cart': {
        push({ ecommerce: null })
        push({
          event: 'remove_from_cart',
          event_id,
          ecommerce: {
            currency: payload.currency,
            value: payload.value,
            items: payload.items?.map(toGa4Item) ?? [],
          },
        })
        break
      }

      case 'view_cart': {
        push({ ecommerce: null })
        push({
          event: 'view_cart',
          event_id,
          ecommerce: {
            currency: payload.currency,
            value: payload.value,
            items: payload.items?.map(toGa4Item) ?? [],
          },
        })
        break
      }

      case 'begin_checkout': {
        push({ ecommerce: null })
        push({
          event: 'begin_checkout',
          event_id,
          ecommerce: {
            currency: payload.currency,
            value: payload.value,
            items: payload.items?.map(toGa4Item) ?? [],
          },
        })
        break
      }

      case 'add_payment_info': {
        push({ ecommerce: null })
        push({
          event: 'add_payment_info',
          event_id,
          ecommerce: {
            payment_type: payload.payment_type,
          },
        })
        break
      }

      case 'add_to_wishlist': {
        push({ ecommerce: null })
        push({
          event: 'add_to_wishlist',
          event_id,
          ecommerce: {
            currency: payload.currency,
            value: payload.value,
            items: payload.items?.map(toGa4Item) ?? [],
          },
        })
        break
      }

      case 'generate_lead': {
        push({ event: 'generate_lead', event_id })
        break
      }

      case 'sign_up': {
        push({ event: 'sign_up', event_id, method: payload.method })
        break
      }

      case 'login': {
        push({ event: 'login', event_id, method: payload.method })
        break
      }

      default:
        break
    }
  }
}
